import { isAndroid } from 'react-device-detect';

import { isNativeAndroid, isNativeSocialLoginBridgeAvailable, sendToNative } from './NativeUtil';
import baseUrl from './baseUrl';
import { getCredentialFromNativeKakao, loginWithKakaoAccessToken } from './kakaoWebSdk';
import { loadScript } from './loadScript';
import type { SocialLoginCredentialInput } from '../types/socialLoginTypes';
import type { SocialProvider } from '../types/socialLoginTypes';

const GOOGLE_GSI_SCRIPT_ID = 'google-gsi-script';
const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_OAUTH_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const APPLE_SDK_SCRIPT_ID = 'apple-signin-sdk-script';
const APPLE_SDK_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const APPLE_OAUTH_WEBHOOK_PATH = '/webhooks/v1/oauth_callback';
const APPLE_WEB_POPUP_REDIRECT_PATH = '/auth/apple/callback';

const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk';
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const FACEBOOK_GRAPH_API_VERSION = 'v24.0';

const WEB_CREDENTIAL_TIMEOUT_MS = 15000;

let googleScriptPromise: Promise<void> | null = null;
let appleScriptPromise: Promise<void> | null = null;
let facebookScriptPromise: Promise<void> | null = null;

function loadGoogleScript() {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(GOOGLE_GSI_SCRIPT_ID, GOOGLE_GSI_SCRIPT_SRC);
  }
  return googleScriptPromise;
}

function loadAppleScript() {
  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(APPLE_SDK_SCRIPT_ID, APPLE_SDK_SCRIPT_SRC);
  }
  return appleScriptPromise;
}

function getMetaAppId() {
  return import.meta.env.REACT_APP_META_APP_ID || '';
}

function loadFacebookScript(): Promise<void> {
  if (facebookScriptPromise) {
    return facebookScriptPromise;
  }

  const appId = getMetaAppId();
  if (!appId) {
    return Promise.reject(new Error('REACT_APP_META_APP_ID is not set'));
  }

  facebookScriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve();
      return;
    }

    window.fbAsyncInit = () => {
      window.FB?.init({
        appId,
        cookie: true,
        xfbml: true,
        version: FACEBOOK_GRAPH_API_VERSION
      });
      resolve();
    };

    loadScript(FACEBOOK_SDK_SCRIPT_ID, FACEBOOK_SDK_SRC).catch(reject);
  });

  return facebookScriptPromise;
}

function getGoogleClientId() {
  return import.meta.env.REACT_APP_GOOGLE_CLIENT_ID || '';
}

function getAppleClientId() {
  return import.meta.env.REACT_APP_APPLE_CLIENT_ID || '';
}

function resolveEnvUrl(envValue: string, origin: string) {
  if (!envValue) return '';
  if (/^https?:\/\//i.test(envValue)) {
    return envValue;
  }
  if (!origin) {
    return envValue;
  }
  const normalizedPath = envValue.startsWith('/') ? envValue : `/${envValue}`;
  return new URL(normalizedPath, origin).toString();
}

function getAppleRedirectUri(usePopup: boolean) {
  const origin = typeof window !== 'undefined' ? window.location?.origin || '' : '';

  if (usePopup) {
    const popupFromEnv = import.meta.env.REACT_APP_APPLE_POPUP_REDIRECT_URI?.trim() || '';
    if (popupFromEnv) {
      return resolveEnvUrl(popupFromEnv, origin);
    }

    if (!origin || typeof window === 'undefined') {
      return '';
    }

    return new URL(APPLE_WEB_POPUP_REDIRECT_PATH, origin).toString();
  }

  const envValue = import.meta.env.REACT_APP_APPLE_REDIRECT_URI?.trim() || '';

  if (envValue) {
    return resolveEnvUrl(envValue, origin);
  }

  const apiHost = baseUrl();
  if (apiHost) {
    return `${String(apiHost).replace(/\/$/, '')}${APPLE_OAUTH_WEBHOOK_PATH}`;
  }

  return '';
}

export function getGoogleRedirectUri() {
  const envValue = import.meta.env.REACT_APP_GOOGLE_REDIRECT_URI?.trim() || '';
  const origin = typeof window !== 'undefined' ? window.location?.origin || '' : '';

  if (envValue) {
    return resolveEnvUrl(envValue, origin);
  }

  if (origin) {
    return `${origin}/auth/social/callback`;
  }

  return '';
}

function buildOAuthCodeAuthorizeUrl(options: {
  authorizeBase: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  statePrefix: 'google' | 'facebook';
  isPromotionAgreed?: boolean;
}): string | null {
  const { authorizeBase, clientId, redirectUri, scope, statePrefix, isPromotionAgreed = false } =
    options;
  if (!clientId || !redirectUri) return null;

  const nonce = createRandomString(24);
  const state = `${statePrefix}_${nonce}_${isPromotionAgreed ? '1' : '0'}`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope,
    state
  });

  return `${authorizeBase}?${params.toString()}`;
}

export function buildFacebookOAuthUrl(isPromotionAgreed = false): string | null {
  const redirectUri = getGoogleRedirectUri();
  return buildOAuthCodeAuthorizeUrl({
    authorizeBase: `https://www.facebook.com/${FACEBOOK_GRAPH_API_VERSION}/dialog/oauth`,
    clientId: getMetaAppId(),
    redirectUri,
    scope: 'email,public_profile',
    statePrefix: 'facebook',
    isPromotionAgreed
  });
}

export function parseProviderFromState(state: string | null): string {
  if (!state) return 'google';
  const parts = state.split('_');
  const prefix = parts[0];
  if (prefix === 'google' || prefix === 'facebook' || prefix === 'apple') {
    return prefix;
  }
  return 'google';
}

export function parsePromotionAgreedFromState(state: string | null): boolean {
  if (!state) return false;

  const parts = state.split('_');
  const promotionFlag = parts[2];
  return promotionFlag === '1';
}

export function buildGoogleOAuthUrl(isPromotionAgreed = false): string | null {
  const redirectUri = getGoogleRedirectUri();
  return buildOAuthCodeAuthorizeUrl({
    authorizeBase: GOOGLE_OAUTH_AUTH_URL,
    clientId: getGoogleClientId(),
    redirectUri,
    scope: 'openid email profile',
    statePrefix: 'google',
    isPromotionAgreed
  });
}

export function requestNativeSocialLogin(provider: 'google' | 'facebook', authUrl: string): void {
  if (!isNativeSocialLoginBridgeAvailable()) {
    window.location.href = authUrl;
    return;
  }

  sendToNative(
    JSON.stringify({
      type: 'nativeSocialLogin',
      provider,
      authUrl
    })
  );
}

function createRandomString(length = 32) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);

    return Array.from(randomValues)
      .map((value) => alphabet[value % alphabet.length])
      .join('');
  }

  return Array.from({ length })
    .map(() => alphabet[Math.floor(Math.random() * alphabet.length)])
    .join('');
}

function runWebCredentialWithTimeout(
  run: (settle: (value: SocialLoginCredentialInput | null) => void) => void
): Promise<SocialLoginCredentialInput | null> {
  return new Promise<SocialLoginCredentialInput | null>((resolve) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, WEB_CREDENTIAL_TIMEOUT_MS);

    const settle = (value: SocialLoginCredentialInput | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(value);
    };

    run(settle);
  });
}

function loginWithGoogleAccessTokenPopup(): Promise<SocialLoginCredentialInput | null> {
  const clientId = getGoogleClientId();
  if (!clientId) return Promise.resolve(null);

  return loadGoogleScript()
    .then(() => {
      const googleOauth2 = window.google?.accounts?.oauth2;
      if (!googleOauth2) return null;

      return runWebCredentialWithTimeout((settle) => {
        const tokenClient = googleOauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: (response) => {
            if (response?.access_token) {
              settle({ accessToken: response.access_token });
              return;
            }

            settle(null);
          },
          error_callback: () => {
            settle(null);
          }
        });

        tokenClient.requestAccessToken({ prompt: 'select_account' });
      });
    })
    .catch(() => null);
}

function loginWithFacebookAccessToken(): Promise<SocialLoginCredentialInput | null> {
  const appId = getMetaAppId();
  if (!appId) return Promise.resolve(null);

  return loadFacebookScript()
    .then(() => {
      const FB = window.FB;
      if (!FB) return null;

      return runWebCredentialWithTimeout((settle) => {
        FB.login(
          (response: { authResponse?: { accessToken?: string } }) => {
            if (response?.authResponse?.accessToken) {
              settle({ accessToken: response.authResponse.accessToken });
              return;
            }
            settle(null);
          },
          {
            scope: 'email,public_profile'
          }
        );
      });
    })
    .catch(() => null);
}

function loginWithAppleCredential(): Promise<SocialLoginCredentialInput | null> {
  const usePopup = !isAndroid;
  const clientId = getAppleClientId();
  const redirectUri = getAppleRedirectUri(usePopup);
  if (!clientId || !redirectUri) {
    return Promise.resolve(null);
  }

  return loadAppleScript()
    .then(async () => {
      const appleAuth = window.AppleID?.auth;
      if (!appleAuth) {
        return null;
      }

      try {
        appleAuth.init({
          clientId,
          scope: 'name email',
          redirectURI: redirectUri,
          state: `apple_${createRandomString(24)}`,
          nonce: createRandomString(24),
          usePopup,
          responseType: 'code id_token'
        });

        const response = await appleAuth.signIn();
        const authorization = response?.authorization;
        if (!authorization) {
          return null;
        }

        return {
          ...(authorization.id_token ? { idToken: authorization.id_token } : {}),
          ...(authorization.code ? { authorizationCode: authorization.code } : {}),
          redirectUri
        };
      } catch (_error) {
        return null;
      }
    })
    .catch(() => null);
}

export function getKakaoCredentialWithBridgeFallback(): Promise<SocialLoginCredentialInput | null> {
  if (!isNativeSocialLoginBridgeAvailable()) {
    return getSocialCredentialFromWeb('kakao');
  }

  return getCredentialFromNativeKakao().then((cred) => {
    if (cred) return cred;
    if (isNativeAndroid()) {
      return getSocialCredentialFromWeb('kakao');
    }
    return null;
  });
}

export function getSocialCredentialFromWeb(
  provider: SocialProvider
): Promise<SocialLoginCredentialInput | null> {
  if (provider === 'google') {
    return loginWithGoogleAccessTokenPopup();
  }

  if (provider === 'kakao') {
    return loginWithKakaoAccessToken();
  }

  if (provider === 'apple') {
    return loginWithAppleCredential();
  }

  if (provider === 'facebook') {
    return loginWithFacebookAccessToken();
  }

  return Promise.resolve(null);
}
